// Surface 05 — la fiche consultant, la recherche dans l'autre sens.
//
// Réutilise la matrice déjà calculée : aucun appel IA. L'historique est le
// journal signé rendu lisible.
//
// La fiche montre aussi ce dont tout le reste dépend et qui n'y figurait
// pas : le CV MAÎTRE lui-même (rendu par le moteur, comme partout ailleurs),
// son PDF d'origine, de quoi le remplacer, et la liste de tous les CV adaptés
// produits en son nom.

import { useState, type CSSProperties } from 'react'
import { ChevronLeft, FileText, Files, RefreshCw, ExternalLink, Pencil, BarChart3, Sparkles, History, Building2 } from 'lucide-react'
import { DocumentPreview, type PreviewDoc } from '@ai-job-fit/ui'
import { renderResumeToHTML, paginatePreview } from '@ai-job-fit/engine/resumeHtml'
import { LANGUE_DOCUMENT } from '@ai-job-fit/engine/langues'
import { get } from '../lib/api'
import { fullDate, relTime, initials, teinte, consultantStatus, candidatureStatus, eventLabels } from '../lib/format'
import { useFormule } from '../lib/formule'
import { useLang } from '../lib/lang'
import { hrefListe, navigate } from '../lib/router'
import { useData, Loading, Empty, Score, ImportCv, PastilleTitre } from '../components/common'
import { ManquesCv } from '../components/manquesCv'
import { MaMiseEnPage, modeleRadar } from '../components/miseEnPage'
import { Categories } from '../components/categories'
import { EditeurCv } from '../components/editeurCv'
import { useMots } from '../lib/vocabulaire'
import { useT } from '../lib/i18n'

interface Detail {
  id: number
  name: string
  label: string
  status: string
  archived: boolean
  location: any
  summary: string
  skills: any[]
  profile: any
  cv_filename: string | null
  /** Le PDF source est-il réellement présent sur le disque ? */
  cv_available: boolean
  updated_at: string
  stats: {
    scored: number
    avg_score: number | null
    best_score: number | null
    go_count: number
    positioned: number
    poolAvg: number | null
  }
  /** Mots-clés déjà couverts / le plus souvent manquants, sur toutes ses
   *  évaluations. Affiché en Radar SEULEMENT (voir le site d'appel) : côté
   *  Console ce serait un agrégat par consultant de plus, quand la question
   *  qui compte pour un BM est déjà répondue par 'ce que couvre la fiche'. */
  motsCles: { couverts: { mot: string; n: number }[]; manquants: { mot: string; n: number }[] }
  offers: { offer_id: number; title: string; company: string; location: string | null; first_seen_at: string; score: number }[]
  dossiers: { id: number; offer_id: number; title: string; company: string; created_at: string; fidelity: number | null; offer_archived: string | null }[]
  timeline: { id: number; type: string; author: string; payload: string | null; created_at: string }[]
}

export function ConsultantPage({ id, monProfil = false }: { id: number; monProfil?: boolean }) {
  const fiche = useData(() => get<Detail>(`/api/consultants/${id}`), [id])
  const mots = useMots()
  const t = useT()
  const formule = useFormule()
  const lang = useLang()
  // « Sur le banc » et « en mission » décrivent la position d'un salarié dans
  // une société de consultance. Un particulier est en recherche, ou en poste.
  const STATUTS = consultantStatus(formule, lang)
  const CAND = candidatureStatus(formule, lang)
  const EVENEMENTS = eventLabels(formule, lang)
  const [majOpen, setMajOpen] = useState(false)
  const [edition, setEdition] = useState(false)
  // Mon CV en Radar : le CV s'affiche avec le modèle et la couleur du compte,
  // et suit le choix fait dans le panneau « Mise en page » (issue #235).
  const radarMoi = formule === 'radar' && monProfil
  const reglages = useData(
    () => (radarMoi ? get<{ template: string; accentColor: string }>('/api/settings') : Promise.resolve(null)),
    [radarMoi]
  )
  const [modeleChoisi, setModeleChoisi] = useState<string | null>(null)
  const [modelePerso, setModelePerso] = useState<string | null>(null)
  const modele = modeleRadar(modeleChoisi ?? reglages.data?.template)

  if (fiche.loading) return <Loading label={t('consultant.loading')} />
  if (fiche.error || !fiche.data) return <Empty>{fiche.error ?? mots('profil.introuvable')}</Empty>

  const c = fiche.data
  const st = STATUTS[c.archived ? 'archived' : c.status] ?? STATUTS.available
  const skills = (c.skills ?? []).map((s: any) => (typeof s === 'string' ? s : s?.name)).filter(Boolean)

  // Le CV maître, rendu par le MÊME moteur que les dossiers : ce qui s'affiche
  // ici est exactement ce que l'adaptation prendra en entrée. Un résumé de
  // champs bricolé dans la page aurait fini par montrer autre chose.
  // Titres de section et « poste actuel » dans la langue de l'interface : sans
  // elle, le moteur retombe sur le français, même pour un compte en anglais.
  const cvDocs: PreviewDoc[] = c.profile
    ? [{
        key: 'cv',
        label: mots('profil.cvMaitre'),
        html: renderResumeToHTML(c.profile, {
          outputLanguage: LANGUE_DOCUMENT[lang],
          ...(radarMoi
            ? { template: modele, accentColor: reglages.data?.accentColor, miseEnPage: modelePerso ?? undefined }
            : { template: 'ATS-Optimized' }),
        }),
        paginate: true,
      }]
    : []

  return (
    <>
      <div className="bar">
        {/* Pas de retour au vivier dans Radar : il n'y a pas de vivier, et le
            bouton mènerait à une page qui ne rend rien. Cette fiche EST la
            destination, pas une escale. */}
        {!monProfil && (
          <button className="back" onClick={() => navigate(hrefListe('vivier'))}>
            <ChevronLeft size={15} />{t('consultant.backToBench')}
          </button>
        )}
      </div>

      <div className="body">
        <div className="cfiche">
          <div className="cprofile">
            <span className="vav" style={teinte(c.name)}>{initials(c.name)}</span>
            <div>
              <div className="cn">
                {c.name} <span className={`pill ${st.cls}`}>{st.label}</span>
              </div>
              <div className="cr">
                {c.label}
                {c.location?.city ? ` · ${c.location.city}` : ''}
              </div>
              <div className="cskills">
                {skills.slice(0, 10).map((s: string) => <span className="tagx" key={s} style={teinte(s)}>{s}</span>)}
              </div>
            </div>
          </div>

          <div className="panel" style={{ gridColumn: '1 / -1' }}>
            <h4>
              <PastilleTitre h={280}><FileText size={16} /></PastilleTitre>
              {mots('profil.cv')}
              <span className="hnote">{t('consultant.updatedOn', { date: fullDate(c.updated_at) })}</span>
            </h4>
            {edition && c.profile ? (
              <EditeurCv
                consultantId={c.id}
                profile={c.profile}
                onFini={(enregistre) => {
                  setEdition(false)
                  if (enregistre) fiche.reload()
                }}
              />
            ) : cvDocs.length === 0 ? (
              // LE BOUTON D'IMPORT ÉTAIT ENFERMÉ DANS `DocumentPreview`, qui ne
              // se rend que s'il y a déjà un CV. Un profil sans CV affichait
              // donc « importe son CV » et n'offrait AUCUN moyen de le faire :
              // un cul-de-sac, dans le seul état où l'action est indispensable.
              <div className="etat">
                <Empty>{mots('profil.cvVide')}</Empty>
                <button className="btn-primary" onClick={() => setMajOpen(true)}>
                  <RefreshCw size={15} />{mots('profil.importer')}
                </button>
              </div>
            ) : (
              <DocumentPreview
                docs={cvDocs}
                activeKey="cv"
                onSelect={() => {}}
                paginate={paginatePreview}
                height="52vh"
                labels={{ print: t('consultant.printPdf'), empty: t('consultant.noDocument') }}
                actions={
                  <>
                    <button className="btn-primary" onClick={() => setEdition(true)}>
                      <Pencil size={15} />{t('consultant.cvEdit')}
                    </button>
                    <button className="btn-secondary" onClick={() => setMajOpen(true)}>
                      <RefreshCw size={15} />{mots('profil.importer')}
                    </button>
                    {c.cv_available && (
                      <a
                        className="btn-secondary"
                        href={`/api/consultants/${c.id}/cv.pdf`}
                        target="_blank"
                        rel="noreferrer"
                        title={c.cv_filename ?? undefined}
                      >
                        <ExternalLink size={15} />{t('consultant.originalPdf')}
                      </a>
                    )}
                  </>
                }
              />
            )}
            <div className="pnote">
              {mots('profil.noteReimport')}
            </div>
          </div>

          {/* Radar (lot 3) : plus de score moyen. Un particulier ne se compare
              à personne ; ce qui l'aide est la liste des manques, juste après. */}
          {formule !== 'radar' && (
          <div className="panel">
            <h4><PastilleTitre h={215}><BarChart3 size={16} /></PastilleTitre>{mots('profil.chiffres')}</h4>
            <div className="cstats">
              <Tile h={215} value={c.stats.avg_score ?? '…'} label={mots('profil.scoreMoyen')} />
              <Tile h={280} value={`×${c.stats.positioned}`} label={mots('profil.positionnements')} />
              <Tile h={150} value={c.stats.best_score ?? '…'} label={mots('profil.meilleur')} />
              <Tile h={40} value={c.stats.scored} label={mots('profil.offresEvaluees')} />
            </div>
            {/* LA BARRE DE COMPARAISON N'EXISTE PAS EN RADAR, et ce n'est pas
                une question de vocabulaire. `poolAvg` est la moyenne de TOUS
                les scores de la base, donc de tous les autres comptes : la
                montrer à un particulier lui apprend où il se situe face à des
                gens qu'il ne connaît pas et contre qui il ne concourt pas.
                C'est la seule chose dont on a dit qu'il ne devait jamais rien
                savoir. Le libellé « Moyenne de mes offres » était en plus faux :
                il ne s'agit pas de ses offres à lui. Pour un business manager
                qui arbitre entre quinze profils, la même barre est utile. */}
            {c.stats.avg_score != null && (
              <div className="compare">
                <Bar name={c.name.split(' ')[0]} value={c.stats.avg_score} cls="me" />
                <Bar name={mots('profil.moyenneVivier')} value={c.stats.poolAvg ?? 0} cls="avg" />
              </div>
            )}
          </div>
          )}

          {/* EN PLUS des chiffres ci-dessus, pas à leur place : un BM arbitre
              entre profils, un particulier veut savoir ce qui revient dans
              SES offres. Les deux questions sont légitimes, chacune dans sa
              formule. Vide (aucune évaluation encore) : le panneau ne
              s'affiche pas, il n'y a rien à en dire de plus que le compteur
              « offres évaluées » juste au-dessus. */}
          {radarMoi && <MaMiseEnPage modele={modele} onModele={setModeleChoisi} onMiseEnPage={setModelePerso} />}
          {formule === 'radar' && monProfil && <ManquesCv onChanged={fiche.reload} />}

          <div className="panel">
            <h4>
              <PastilleTitre h={280}><Files size={16} /></PastilleTitre>
              {mots('profil.cvAdaptes')}
              {c.dossiers.length > 0 && <span className="hnote">{t('consultant.filesTotal', { n: c.dossiers.length })}</span>}
            </h4>
            {c.dossiers.length === 0 ? (
              <Empty>{mots('profil.aucunDossier')}</Empty>
            ) : (
              c.dossiers.map((d) => (
                <div
                  className="rmatch"
                  key={d.id}
                  onClick={() => navigate(`#/offre/${d.offer_id}/dossier/${d.id}`)}
                >
                  <span className="miniav" style={teinte(d.company)}><FileText size={14} /></span>
                  <div className="ro">
                    <div className="rc">{d.company ? `${d.company} : ` : ''}{d.title}</div>
                    <div className="rt">
                      {relTime(d.created_at)}
                      {d.offer_archived ? ` · ${t('consultant.offerArchived')}` : ''}
                    </div>
                  </div>
                  {/* Un pourcentage nu ne dit pas de quoi il parle : le titre
                      le nomme pour la souris comme pour un lecteur d'écran. */}
                  {d.fidelity != null && (
                    <span className="fid" title={t('consultant.fidelityTitle', { n: d.fidelity })}>
                      {d.fidelity}%
                    </span>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="panel">
            <h4><PastilleTitre h={190}><Sparkles size={16} /></PastilleTitre>{mots('profil.offresQuiCollent')}</h4>
            {c.offers.length === 0 ? (
              <Empty>{mots('profil.aucuneOffre')}</Empty>
            ) : (
              c.offers.map((o) => (
                <div className="rmatch" key={o.offer_id} onClick={() => navigate(`#/offre/${o.offer_id}`)}>
                  <span className="miniav" style={teinte(o.company ?? '')}>{o.company ? initials(o.company) : <Building2 size={14} />}</span>
                  <div className="ro">
                    <div className="rc">{o.company ? `${o.company} : ` : ''}{o.title}</div>
                    <div className="rt">{o.location ? `${o.location} · ` : ''}{relTime(o.first_seen_at)}</div>
                  </div>
                  <Score value={o.score} />
                </div>
              ))
            )}
          </div>

          {/* L'historique technique reste à la console (lot 3). */}
          {formule !== 'radar' && (
          <div className="panel">
            <h4><PastilleTitre h={40}><History size={16} /></PastilleTitre>{mots('profil.historique')}</h4>
            {c.timeline.length === 0 ? (
              <Empty>{t('consultant.noEvent')}</Empty>
            ) : (
              <div className="timeline">
                {c.timeline.map((ev) => {
                  const p = ev.payload ? JSON.parse(ev.payload) : {}
                  const extra =
                    ev.type === 'dossier.generated' ? t('consultant.eventFidelity', { n: p.fidelity ?? '—' })
                    : ev.type === 'consultant.placed' && p.client ? t('consultant.eventClient', { client: p.client })
                    // L'étape est dans le payload : sans elle, six changements
                    // d'un dossier s'affichent six fois à l'identique.
                    : ev.type === 'candidature.status' && p.status
                      ? ` : ${CAND[p.status]?.label ?? p.status}`
                    : ''
                  return (
                    <div className={`tev${p.status === 'place' ? ' teal' : ''}`} key={ev.id}>
                      <div className="td">{fullDate(ev.created_at)} · {ev.author}</div>
                      <div className="tt"><b>{EVENEMENTS[ev.type] ?? ev.type}</b>{extra}</div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
          )}
        </div>

        {/* Les catégories décident de quelles offres arrivent jusqu'à ce
            profil. Leur place est donc ici, sur la fiche, pas dans un écran de
            réglages : c'est une propriété de la personne, pas du produit. */}
        <Categories base={`/api/consultants/${id}`} onChanged={fiche.reload} />
      </div>

      {majOpen && (
        <ImportCv
          preset={c.name}
          onClose={() => setMajOpen(false)}
          onDone={() => fiche.reload()}
        />
      )}
    </>
  )
}

/** Une tuile de chiffre, teintée (`h`) pour que les quatre se distinguent
 *  d'un coup d'œil plutôt que par leur libellé. */
function Tile({ value, label, h }: { value: any; label: string; h: number }) {
  return (
    <div className="ctile" style={{ '--h': h } as CSSProperties}>
      <div className="cv">{value}</div>
      <div className="cl">{label}</div>
    </div>
  )
}

function Bar({ name, value, cls }: { name: string; value: number; cls: string }) {
  return (
    <div className={`crow ${cls}`}>
      <span className="cnm">{name}</span>
      <span className="ctrack"><span className="cf" style={{ width: `${value}%` }} /></span>
      <span className="cval">{value}</span>
    </div>
  )
}

